import type { DashboardPartnerLeaderboardSchema } from "@biteasy/contracts";
import { ResponsiveBar } from "@nivo/bar";
import type { z } from "zod";
import { formatCurrency } from "@/util/format";

interface PartnerLeaderboardProps {
	data: z.infer<typeof DashboardPartnerLeaderboardSchema>;
	currency?: string;
}

/**
 * Horizontal bar chart showing top partners by earnings
 */
export function PartnerLeaderboard({
	data,
	currency = "usd",
}: PartnerLeaderboardProps) {
	if (data.items.length === 0) {
		return (
			<div className="flex h-60 items-center justify-center rounded-xl border border-dashed border-base-content/20 bg-base-100/50">
				<p className="text-sm text-base-content/60">No partner data yet</p>
			</div>
		);
	}

	const chartData = data.items.map((item) => ({
		partner: item.partnerName,
		earnings: item.earnings / 100, // Convert to dollars
	}));

	return (
		<div className="h-60 rounded-xl border border-base-content/10 bg-base-100 p-4">
			<ResponsiveBar
				data={chartData}
				keys={["earnings"]}
				indexBy="partner"
				layout="horizontal"
				margin={{ top: 10, right: 20, bottom: 30, left: 100 }}
				padding={0.3}
				valueScale={{ type: "linear" }}
				indexScale={{ type: "band", round: true }}
				colors={["#10b981"]}
				borderRadius={4}
				borderColor={{ from: "color", modifiers: [["darker", 0.2]] }}
				axisTop={null}
				axisRight={null}
				axisBottom={{
					tickSize: 5,
					tickPadding: 5,
					tickRotation: 0,
					format: (v) => `$${v}`,
				}}
				axisLeft={{
					tickSize: 0,
					tickPadding: 10,
					tickRotation: 0,
				}}
				enableGridX={true}
				enableGridY={false}
				labelSkipWidth={60}
				labelSkipHeight={12}
				labelTextColor={{
					from: "color",
					modifiers: [["darker", 2]],
				}}
				tooltip={({ data: d, value }) => (
					<div className="rounded-lg border border-base-content/10 bg-base-100 p-3 shadow-xl">
						<div className="text-sm font-medium text-base-content">
							{d.partner}
						</div>
						<div className="mt-1 text-sm font-bold text-base-content">
							{formatCurrency(value * 100, currency)}
						</div>
					</div>
				)}
				theme={{
					background: "transparent",
					text: {
						fill: "var(--color-base-content)",
						fontSize: 12,
					},
					axis: {
						domain: {
							line: {
								stroke: "var(--color-base-content)",
								strokeOpacity: 0.2,
								strokeWidth: 1,
							},
						},
						ticks: {
							line: {
								stroke: "var(--color-base-content)",
								strokeOpacity: 0.2,
								strokeWidth: 1,
							},
							text: {
								fill: "var(--color-base-content)",
								fillOpacity: 0.6,
							},
						},
					},
					grid: {
						line: {
							stroke: "var(--color-base-content)",
							strokeOpacity: 0.08,
							strokeDasharray: "4 4",
						},
					},
				}}
			/>
		</div>
	);
}
